import type {
  FacesheetConditionSortDirection,
  FacesheetConditionSortMode,
} from '@seen/core/models/conditions/Diagnosis'
import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'

const SORT_MODES: Record<FacesheetConditionSortMode, true> = {
  anatomical: true,
  chronological: true,
  alphabetical: true,
}

export function isFacesheetConditionSortMode(
  value: unknown
): value is FacesheetConditionSortMode {
  return typeof value === 'string' && Object.hasOwn(SORT_MODES, value)
}

export function isSortDirection(
  value: unknown
): value is FacesheetConditionSortDirection {
  return value === 'asc' || value === 'desc'
}

interface FacesheetConditionSort {
  sortMode: FacesheetConditionSortMode
  chronologicalDirection: FacesheetConditionSortDirection
  alphabeticalDirection: FacesheetConditionSortDirection
}

export const DEFAULT_FACESHEET_CONDITION_SORT: FacesheetConditionSort = {
  sortMode: 'anatomical',
  chronologicalDirection: 'desc',
  alphabeticalDirection: 'asc',
}

export const FACESHEET_CONDITION_SORT_STORAGE_NAME =
  'facesheet-condition-sort-store'

export const useFacesheetConditionSortStore = create(
  devtools(
    persist(
      combine({ ...DEFAULT_FACESHEET_CONDITION_SORT }, (set) => ({
        setSortMode: (sortMode: FacesheetConditionSortMode) => {
          set({ sortMode })
        },
        setChronologicalDirection: (
          chronologicalDirection: FacesheetConditionSortDirection
        ) => {
          set({ chronologicalDirection })
        },
        setAlphabeticalDirection: (
          alphabeticalDirection: FacesheetConditionSortDirection
        ) => {
          set({ alphabeticalDirection })
        },
      })),
      {
        name: FACESHEET_CONDITION_SORT_STORAGE_NAME,
        version: 1,
        partialize: (state) => ({
          sortMode: state.sortMode,
          chronologicalDirection: state.chronologicalDirection,
          alphabeticalDirection: state.alphabeticalDirection,
        }),
        merge: (persisted, current) => {
          const stored = persisted as
            | {
                sortMode?: unknown
                chronologicalDirection?: unknown
                alphabeticalDirection?: unknown
              }
            | undefined
          return {
            ...current,
            sortMode: isFacesheetConditionSortMode(stored?.sortMode)
              ? stored.sortMode
              : current.sortMode,
            chronologicalDirection: isSortDirection(
              stored?.chronologicalDirection
            )
              ? stored.chronologicalDirection
              : current.chronologicalDirection,
            alphabeticalDirection: isSortDirection(
              stored?.alphabeticalDirection
            )
              ? stored.alphabeticalDirection
              : current.alphabeticalDirection,
          }
        },
      }
    )
  )
)
